import React from "react";
import { HiCheck } from "react-icons/hi";
import { FaCheckCircle } from "react-icons/fa";
import { cn } from "@/lib/utils";

type FeaturesProps = {
  plans: string[];
};

type FeatureItem = {
  name: string;
  hobby: string | boolean;
  clipper: string | boolean;
  pro: string | boolean;
};

type FeatureSection = {
  title: string;
  features: FeatureItem[];
};

const FEATURE_SECTIONS: FeatureSection[] = [
  {
    title: "AI Workflow",
    features: [
      {
        name: "Workflow credits",
        hobby: "40 credits included",
        clipper: "120 credits included",
        pro: "180 credits included",
      },
      {
        name: "Fake texts video",
        hobby: true,
        clipper: true,
        pro: true,
      },
      {
        name: "Reddit video",
        hobby: true,
        clipper: true,
        pro: true,
      },
      {
        name: "Split-screen video",
        hobby: true,
        clipper: true,
        pro: true,
      },
      {
        name: "Streamer video",
        hobby: true,
        clipper: true,
        pro: true,
      },
      {
        name: "ChatGPT video",
        hobby: true,
        clipper: true,
        pro: true,
      },
    ],
  },
  {
    title: "Crayo tools",
    features: [
      {
        name: "AI voiceovers",
        hobby: "30 minutes included",
        clipper: "120 minutes included",
        pro: "180 minutes included",
      },
      {
        name: "AI images",
        hobby: "100 credits included",
        clipper: "300 credits included",
        pro: "500 credits included",
      },
      {
        name: "VEO3 Credits",
        hobby: "Requires topup purchase",
        clipper: "Requires topup purchase",
        pro: "Requires topup purchase",
      },
      {
        name: "Vocal & Instrumental Remover",
        hobby: true,
        clipper: true,
        pro: true,
      },
      {
        name: "AI Brainstorm",
        hobby: true,
        clipper: true,
        pro: true,
      },
      {
        name: "YouTube downloader",
        hobby: true,
        clipper: true,
        pro: true,
      },
      {
        name: "TikTok downloader",
        hobby: true,
        clipper: true,
        pro: true,
      },
    ],
  },
];

// Component for rendering feature value (checkmark or text)
const FeatureValue: React.FC<{ value: string | boolean; plan: string }> = ({ value, plan }) => {
  if (typeof value === "boolean" && value) {
    const isClipperPlan = plan === "Clipper";

    return isClipperPlan ? (
      <FaCheckCircle className="mx-auto h-4 w-4 text-green-500 md:h-5 md:w-5" />
    ) : (
      <div className="mx-auto flex h-4 w-4 items-center justify-center rounded-full border-2 border-gray-900 md:h-5 md:w-5">
        <HiCheck className="h-2.5 w-2.5 text-gray-900 md:h-3 md:w-3" />
      </div>
    );
  }

  return <span className="text-xs text-gray-600 md:text-sm">{value}</span>;
};

// Component for rendering plan headers
const PlanHeader: React.FC<{ plan: string }> = ({ plan }) => (
  <div
    className={cn(
      "text-center text-sm font-medium md:text-base",
      plan === "Clipper" ? "text-blue-600" : "text-gray-900",
    )}
  >
    {plan}
  </div>
);

// Component for rendering a single feature row
const FeatureRow: React.FC<{
  feature: FeatureItem;
  plans: string[];
  isLast: boolean;
}> = ({ feature, plans, isLast }) => (
  <div
    className={cn(
      "grid grid-cols-[minmax(120px,1fr),repeat(3,minmax(100px,1fr))] items-center py-3 md:grid-cols-4 md:py-4",
      !isLast && "border-b border-gray-100",
    )}
  >
    <div className="pr-2 text-sm text-gray-900 md:text-base">{feature.name}</div>
    {plans.map((plan) => {
      const planKey = plan.toLowerCase() as keyof FeatureItem;
      const value = feature[planKey];

      return (
        <div key={plan} className="px-2 text-center">
          <FeatureValue value={value} plan={plan} />
        </div>
      );
    })}
  </div>
);

// Component for rendering a feature section
const FeatureSection: React.FC<{
  section: FeatureSection;
  plans: string[];
  sectionTitle: string;
}> = ({ section, plans, sectionTitle }) => (
  <div className="w-full overflow-hidden rounded-lg border border-gray-100 p-3 md:p-4">
    <div className="overflow-x-auto">
      <div className="min-w-[500px]">
        <div className="grid grid-cols-[minmax(120px,1fr),repeat(3,minmax(100px,1fr))] pb-3 md:grid-cols-4 md:pb-4">
          <div>
            <p className="text-sm font-semibold text-gray-500 md:text-base">{sectionTitle}</p>
          </div>
          {plans.map((plan) => (
            <PlanHeader key={plan} plan={plan} />
          ))}
        </div>

        {section.features.map((feature, index) => (
          <FeatureRow
            key={feature.name}
            feature={feature}
            plans={plans}
            isLast={index === section.features.length - 1}
          />
        ))}
      </div>
    </div>
  </div>
);

const Features: React.FC<FeaturesProps> = ({ plans }) => {
  if (!plans || plans.length === 0) return null;

  const sectionTitles = ["Crayo Workflows", "Crayo Tools"];

  return (
    <div className="w-3/4 items-center justify-center px-3 py-6 md:px-4 md:py-8">
      <h2 className="mb-6 text-2xl font-semibold md:mb-8 md:text-3xl">Features</h2>
      <div className="flex flex-col gap-3 md:gap-4">
        {FEATURE_SECTIONS.map((section, index) => (
          <FeatureSection
            key={section.title}
            section={section}
            plans={plans}
            sectionTitle={sectionTitles[index]}
          />
        ))}
      </div>
    </div>
  );
};

export default Features;
